import React, { useEffect, useState } from 'react';
import apiService from '../services/api';

interface PreviewRow {
  team: string;
  winPoints: number;
  losePoints: number;
  tableRank: number | null;
}

interface PreviewTable {
  missedPenalty: number;
  /** No result / abandoned: missed pick gets this (worst Lose); pickers get 0. */
  abandonedMissedPenalty?: number;
  mode: string;
  rows: PreviewRow[];
}

interface CachedPayload {
  previewTable: PreviewTable;
  snapshotCapturedAt?: string | null;
  snapshotSource?: string | null;
}

interface Props {
  leagueId: number;
  matchId: string;
  /** When true, hydrate from sessionStorage for this league+match and refresh in background. */
  persistSnapshot?: boolean;
}

function cacheKey(leagueId: number, matchId: string) {
  return `cfp:pickemDynamicPreview:v1:${leagueId}:${matchId}`;
}

function formatPts(n: number): string {
  if (n > 0) return `+${n}`;
  return String(n);
}

/**
 * Dynamic Pick'em: points if you pick each team (from latest snapshot before match).
 */
const DynamicPickScoringNote: React.FC<Props> = ({ leagueId, matchId, persistSnapshot }) => {
  const [previewTable, setPreviewTable] = useState<PreviewTable | null>(null);
  const [snapshotCapturedAt, setSnapshotCapturedAt] = useState<string | null>(null);
  const [snapshotSource, setSnapshotSource] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (persistSnapshot) {
      try {
        const raw = sessionStorage.getItem(cacheKey(leagueId, matchId));
        if (raw) {
          const parsed = JSON.parse(raw) as CachedPayload;
          if (parsed?.previewTable?.rows?.length) {
            setPreviewTable(parsed.previewTable);
            setSnapshotCapturedAt(parsed.snapshotCapturedAt ?? null);
            setSnapshotSource(parsed.snapshotSource ?? null);
          }
        }
      } catch {
        /* ignore bad cache */
      }
    }

    (async () => {
      try {
        const res = (await apiService.getPickemDynamicPreview(leagueId, matchId)) as {
          dynamic?: boolean;
          previewTable?: PreviewTable;
          snapshotCapturedAt?: string | null;
          snapshotSource?: string | null;
        };
        if (cancelled) return;
        if (!res.dynamic || !res.previewTable?.rows?.length) {
          setPreviewTable(null);
          setSnapshotCapturedAt(null);
          setSnapshotSource(null);
          return;
        }
        setPreviewTable(res.previewTable);
        setSnapshotCapturedAt(res.snapshotCapturedAt ?? null);
        setSnapshotSource(res.snapshotSource ?? null);
        setErr(null);
        if (persistSnapshot) {
          try {
            const payload: CachedPayload = {
              previewTable: res.previewTable,
              snapshotCapturedAt: res.snapshotCapturedAt ?? null,
              snapshotSource: res.snapshotSource ?? null
            };
            sessionStorage.setItem(cacheKey(leagueId, matchId), JSON.stringify(payload));
          } catch {
            /* quota / private mode */
          }
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Could not load scoring preview';
          setErr(msg);
          if (!persistSnapshot) {
            setPreviewTable(null);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId, matchId, persistSnapshot]);

  if (err && !previewTable) {
    return <p className="text-xs text-amber-700 mt-2">{err}</p>;
  }
  if (!previewTable || !previewTable.rows.length) return null;

  const isPlayoff = previewTable.mode === 'playoff';
  const isFinal = previewTable.mode === 'final';
  const isFixedKnockout = isPlayoff || isFinal;

  const snapshotLine =
    snapshotCapturedAt != null && snapshotCapturedAt !== ''
      ? new Date(snapshotCapturedAt).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short'
        })
      : null;

  return (
    <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-700">
      <p className="text-xs font-semibold text-slate-600 mb-2">Points for this match (if you pick the team)</p>
      {isPlayoff && (
        <p className="text-[11px] text-indigo-700 mb-2 font-medium">Playoff scoring: +10 if correct, −5 if wrong (missed pick −5).</p>
      )}
      {isFinal && (
        <p className="text-[11px] text-indigo-700 mb-2 font-medium">Final scoring: +15 if correct, −10 if wrong (missed pick −10).</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden">
          <thead>
            <tr className="bg-slate-100 text-slate-600">
              <th className="text-left py-1.5 px-2 font-semibold">Team</th>
              {!isFixedKnockout && (
                <th className="text-left py-1.5 px-2 font-normal text-slate-500">Table</th>
              )}
              <th className="text-right py-1.5 px-2 font-semibold">Win</th>
              <th className="text-right py-1.5 px-2 font-semibold">Lose</th>
            </tr>
          </thead>
          <tbody>
            {previewTable.rows.map((r, i) => (
              <tr key={`${r.team}-${i}`} className="border-t border-slate-200 bg-white">
                <td className="py-1.5 px-2 font-medium text-slate-800">{r.team}</td>
                {!isFixedKnockout && (
                  <td className="py-1.5 px-2 text-slate-500">
                    {r.tableRank != null ? `${r.tableRank}${ordinal(r.tableRank)}` : '—'}
                  </td>
                )}
                <td className="py-1.5 px-2 text-right tabular-nums text-green-700 font-medium">
                  {formatPts(r.winPoints)}
                </td>
                <td className="py-1.5 px-2 text-right tabular-nums text-red-700 font-medium">
                  {formatPts(r.losePoints)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">
        Win / Lose = points if that team is your pick and they win or lose. Missed or late pick:{' '}
        <span className="font-medium text-slate-700">{formatPts(previewTable.missedPenalty)}</span>.
        {!isFixedKnockout && (
          <>
            {' '}
            Uses the saved points table snapshot from before this match
            {snapshotLine ? (
              <>
                {' '}
                (<span className="font-medium text-slate-600">{snapshotLine}</span>
                {snapshotSource ? ` · ${snapshotSource}` : ''})
              </>
            ) : (
              ''
            )}
            ; final winner points apply when the result is recorded.
          </>
        )}
        {isFixedKnockout && ' Final winner points apply when the result is recorded.'}
        {previewTable.abandonedMissedPenalty != null && (
          <>
            {' '}
            Abandoned / no result: pickers earn 0; missed pick{' '}
            <span className="font-medium text-slate-700">
              {formatPts(previewTable.abandonedMissedPenalty)}
            </span>
            .
          </>
        )}
      </p>
      {err && persistSnapshot && previewTable && (
        <p className="text-[11px] text-amber-600 mt-1">Could not refresh preview; showing cached values.</p>
      )}
    </div>
  );
}

function ordinal(n: number): string {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

export default DynamicPickScoringNote;
